// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useMemo } from 'react';
import {
  Alert,
  Box,
  Header,
  SpaceBetween,
  Spinner,
  Tabs,
} from '@amzn/awsui-components-react-v3/polaris';
import DetailsPanel from '../../Common/DetailsPanel';
import { RetryAlert } from '../../Common/ErrorMessages';
import ListTable from '../../Common/ListTable';
import { ListColumn } from '../../Common/ListTable';
import { ArcChange, ArcChangeKind, compareArcs, LanguagePair } from '../../../utils/arcRows';
import { Locale } from '../../../utils/locales';
import * as baseRates from '../BaseRates/baseRates';
import { arcLabel } from '../PricingModels/pricingModels';
import { Loaded, LoadClient, useLoaded } from '../ScopingModels/useLoaded';
import * as terGroups from '../TerGroups/terGroups';
import * as changeSets from './changeSets';

/** A per-arc value, shown and compared as text. */
interface ValueColumn<T> {
  id: string;
  header: string;
  value: (item: T) => string;
}

const BASE_PRICE_COLUMNS: ValueColumn<baseRates.BasePrice>[] = [
  { id: 'amount', header: 'Amount', value: (item): string => String(item.price.amount) },
  { id: 'currency', header: 'Currency', value: (item): string => item.price.currency },
];

const TER_COLUMNS: ValueColumn<terGroups.Ter>[] = [
  { id: 'ter', header: 'TER', value: (item): string => String(item.ter) },
];

const CHANGE_LABELS: Record<ArcChangeKind, string> = {
  added: 'Added',
  removed: 'Removed',
  changed: 'Changed',
};

/** "old → new" when a value changed, otherwise the one value; "-" when there is none. */
const transition = (before: string | null, after: string | null): string => {
  if (before !== null && after !== null && before !== after) {
    return `${before} → ${after}`;
  }
  const value = after === null ? before : after;
  return value === null ? '-' : value;
};

function arcColumn<T extends { languagePair: LanguagePair }>(
  locales: Locale[] | null
): ListColumn<T> {
  return { id: 'arc', header: 'Arc', cell: (item): string => arcLabel(item.languagePair, locales) };
}

const valueColumns = <T extends { languagePair: LanguagePair }>(
  columns: ValueColumn<T>[],
  locales: Locale[] | null
): ListColumn<T>[] => [
  arcColumn<T>(locales),
  ...columns.map(({ id, header, value }) => ({ id, header, cell: value })),
];

const changeColumns = <T extends { languagePair: LanguagePair }>(
  columns: ValueColumn<T>[],
  locales: Locale[] | null
): ListColumn<ArcChange<T>>[] => [
  arcColumn<ArcChange<T>>(locales),
  { id: 'change', header: 'Change', cell: (change): string => CHANGE_LABELS[change.kind] },
  ...columns.map(({ id, header, value }) => ({
    id,
    header,
    cell: (change: ArcChange<T>): string =>
      transition(change.before && value(change.before), change.after && value(change.after)),
  })),
];

/** The arcs a change set changes, and those it leaves as they are, in two tabs. */
function ArcTabs<T extends { languagePair: LanguagePair }>({
  id,
  before,
  after,
  columns,
  locales,
}: {
  id: string;
  before: T[];
  after: T[];
  columns: ValueColumn<T>[];
  locales: Locale[] | null;
}): JSX.Element {
  // Memoized so a render does not compare every arc again.
  const { changes, unchanged } = useMemo(
    () =>
      compareArcs(before, after, (beforeItem, afterItem) =>
        columns.every(({ value }) => value(beforeItem) === value(afterItem))
      ),
    [before, after, columns]
  );
  return (
    <Tabs
      tabs={[
        {
          id: 'changes',
          label: `Changes (${changes.length})`,
          content: (
            <ListTable
              id={`${id}Changes`}
              items={changes}
              columns={changeColumns(columns, locales)}
              empty="No arcs change."
            />
          ),
        },
        {
          id: 'unchanged',
          label: `Unchanged (${unchanged.length})`,
          content: (
            <ListTable
              id={`${id}Unchanged`}
              items={unchanged}
              columns={valueColumns(columns, locales)}
              empty="No arcs are unchanged."
            />
          ),
        },
      ]}
    />
  );
}

interface ArcListSpec<T> {
  id: string;
  title: string;
  empty: string;
  columns: ValueColumn<T>[];
}

const BASE_PRICES_SPEC: ArcListSpec<baseRates.BasePrice> = {
  id: 'proposedBasePrices',
  title: 'Proposed base rates',
  empty: 'No base rates.',
  columns: BASE_PRICE_COLUMNS,
};

const TERS_SPEC: ArcListSpec<terGroups.Ter> = {
  id: 'proposedTers',
  title: 'Proposed TERs',
  empty: 'No TERs.',
  columns: TER_COLUMNS,
};

const NO_ITEMS: never[] = [];

/**
 * A proposed per-arc list in Changes and Unchanged tabs against `before`, the arcs of the base
 * version; or listed as it is while that version is loading or failed to load.
 */
function ProposedArcs<T extends { languagePair: LanguagePair }>({
  spec,
  proposed,
  before,
  base,
  baseVersion,
  locales,
}: {
  spec: ArcListSpec<T>;
  proposed: T[];
  // Null until the base version is loaded; empty when there is none.
  before: T[] | null;
  base: Loaded<unknown>;
  baseVersion: number | null;
  locales: Locale[] | null;
}): JSX.Element {
  if (base.missing || base.errors.length > 0) {
    return (
      <SpaceBetween size="m">
        <RetryAlert
          id="baseVersionLoadError"
          header={`Could not load version ${baseVersion} to compare with; showing the proposed values only`}
          messages={base.missing ? [`Version ${baseVersion} was not found.`] : base.errors}
          retryId="retryBaseVersion"
          onRetry={base.reload}
        />
        <ListTable
          id={spec.id}
          items={proposed}
          columns={valueColumns(spec.columns, locales)}
          header={<Header variant="h2">{spec.title}</Header>}
          empty={spec.empty}
        />
      </SpaceBetween>
    );
  }
  if (before === null) {
    return (
      <Box id="baseVersionLoading" textAlign="center">
        <Spinner /> Loading version {baseVersion} to compare with
      </Box>
    );
  }
  return (
    <SpaceBetween size="m">
      <Header
        variant="h2"
        description={
          baseVersion === null
            ? 'There is no live version to compare with.'
            : `Compared with version ${baseVersion}, the version live when it was proposed.`
        }
      >
        {spec.title}
      </Header>
      <ArcTabs
        id={spec.id}
        before={before}
        after={proposed}
        columns={spec.columns}
        locales={locales}
      />
    </SpaceBetween>
  );
}

const MissingValues = (): JSX.Element => (
  <Alert id="proposedValuesMissing" type="warning">
    The proposed values of this change set are missing.
  </Alert>
);

const numberOrNull = (value: number | null | undefined): string | null =>
  value === null || value === undefined ? null : String(value);

const PROPOSED_DEFAULT_TERS: {
  field: terGroups.DefaultTerField;
  proposedField: 'proposedBilingualDefaultTer' | 'proposedMonolingualDefaultTer';
  label: string;
}[] = [
  {
    field: 'bilingualDefaultTer',
    proposedField: 'proposedBilingualDefaultTer',
    label: 'Proposed default TER (bilingual)',
  },
  {
    field: 'monolingualDefaultTer',
    proposedField: 'proposedMonolingualDefaultTer',
    label: 'Proposed default TER (monolingual)',
  },
];

/**
 * What a base-rate or TER-group change set proposes, compared arc by arc with the live version it
 * was proposed from, which is loaded for the comparison.
 */
export const ProposedValues = ({
  apiClient,
  changeSet,
  locales,
}: {
  apiClient: LoadClient;
  changeSet: changeSets.ChangeSetDetail;
  // Labels arcs by locale name; null shows their locale codes.
  locales: Locale[] | null;
}): JSX.Element => {
  const isTerGroup = changeSet.entityType === 'terGroup';
  const proposed = isTerGroup ? changeSet.proposedTers : changeSet.proposedBasePrices;
  const path = proposed ? changeSets.baseVersionPath(changeSet) : null;
  const base = useLoaded<baseRates.BaseRates | terGroups.TerGroup>(
    apiClient,
    path,
    isTerGroup ? terGroups.describeError : baseRates.describeError
  );
  const loaded = path === null || base.value !== null;

  if (!isTerGroup) {
    const baseRate = base.value as baseRates.BaseRates | null;
    const basePrices = changeSet.proposedBasePrices;
    if (!basePrices) {
      return <MissingValues />;
    }
    return (
      <ProposedArcs
        spec={BASE_PRICES_SPEC}
        proposed={basePrices}
        before={loaded ? (baseRate && baseRate.basePrices) || NO_ITEMS : null}
        base={base}
        baseVersion={changeSet.baseVersion}
        locales={locales}
      />
    );
  }

  const baseGroup = base.value as terGroups.TerGroup | null;
  const ters = changeSet.proposedTers;
  return (
    <SpaceBetween size="m">
      <DetailsPanel
        items={PROPOSED_DEFAULT_TERS.map(({ field, proposedField, label }) => {
          const proposed = numberOrNull(changeSet[proposedField]);
          return {
            label,
            value: (
              <span id={proposedField}>
                {proposed === null
                  ? '-'
                  : transition(numberOrNull(baseGroup && baseGroup[field]), proposed)}
              </span>
            ),
          };
        })}
      />
      {ters ? (
        <ProposedArcs
          spec={TERS_SPEC}
          proposed={ters}
          before={loaded ? (baseGroup && baseGroup.ters) || NO_ITEMS : null}
          base={base}
          baseVersion={changeSet.baseVersion}
          locales={locales}
        />
      ) : (
        <MissingValues />
      )}
    </SpaceBetween>
  );
};
